import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';

import { TaskEventDetailModal } from '@/components/domain/shared/TaskEventDetailModal';
import { EmptyState, ErrorState, LoadingSpinner } from '@/components/states';
import { Button } from '@/components/ui';
import { scheduleService } from '@/services/schedule.service';
import { toast, useUserStore } from '@/stores';
import type { EventRow } from '@/lib/schedule';

/**
 * Deep-link target for an SMS digest event link (`/e/:eventId`).
 *
 * Mounted inside <ProtectedRoute><AppShell>, so an unauthenticated hit
 * bounces to `/` (sign-in) and, once onboarding completes, lands the user
 * on `/today`. When authenticated we fetch the event with the same service
 * method Activity uses and open the read-only detail modal over the app
 * shell; closing it returns to `/today`. A deleted/archived event (the
 * fetch resolves null) renders the "removed item" view instead.
 */
export function EventDeepLink() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const userTimezone = useUserStore((s) => s.timezone);

  const [event, setEvent] = useState<EventRow | null>(null);
  const [loading, setLoading] = useState(true);
  // Distinguishes a transient fetch/network failure (retryable error
  // surface) from a genuine not-found where the fetch resolved null (the
  // "removed item" surface, which offers no retry). Bumping `reloadKey`
  // re-runs the effect for the retry.
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!eventId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setLoadError(false);
    scheduleService
      .getEventById(eventId)
      // The EVENT_SELECT row matches EventRow structurally; the cast avoids
      // importing the generated DB types here (same as Activity/TodayTab).
      .then((data) => {
        if (active) setEvent(data ? (data as unknown as EventRow) : null);
      })
      .catch(() => {
        if (!active) return;
        // A network/fetch failure — NOT a genuine 404. Surface a retryable
        // error instead of falsely claiming the item was removed.
        setLoadError(true);
        toast.error('Could not load details. Please try again.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [eventId, reloadKey]);

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (loadError) {
    return (
      <div className="flex min-h-[60dvh] flex-col items-center justify-center">
        <ErrorState
          message="Couldn't load this item."
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-[60dvh] flex-col items-center justify-center">
        <EmptyState
          icon={<Trash2 size={40} />}
          title="This item has been removed."
          action={
            <Button
              variant="outline"
              onClick={() => navigate('/today')}
            >
              Back to Today
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <TaskEventDetailModal
      item={{ kind: 'event', data: event }}
      canEdit={false}
      timezone={userTimezone}
      onClose={() => navigate('/today')}
      /* canEdit is false here, so the pencil and this handler never render */
      onEdit={() => {}}
    />
  );
}
